import React, { useState, useEffect, useCallback } from "react";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Grid,
  Card,
  Box,
  Autocomplete,
  IconButton,
} from "@mui/material";
import axios from "../../api";
import { AxiosError } from "axios";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useDropzone } from "react-dropzone";
import AssigneeHistory from "examples/ReuseFunctions/AssigneeHistory";
import StatusHistory from "examples/ReuseFunctions/StatusHistory";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import StatusTracker from "examples/ReuseFunctions/StatusTracker";
import CircularProgress from "@mui/material/CircularProgress";
import DateFormat from "examples/ReuseFunctions/DateFormat";

interface Customer {
  customer_id: string;
  name: string;
  email?: string;
  type: string;
  is_active?: number | boolean;
}

interface AssetType {
  asset_type_id: string;
  asset_type_name: string;
  is_active?: number | boolean;
}

interface Category {
  category_id: string;
  name: string;
}

interface Asset {
  asset_id: string;
  asset_name: string;
  address_id: string;
  address_type: string;
  city_name: string;
  region_name: string;
  asset_type_id?: string;
  asset_type_name?: string;
  division_id?: number;
  division_name?: string;
  state_name?: string;
  address?: string;
  asset_sku?: string;
  is_active?: number | boolean;
}

interface Employee {
  employee_id: string;
  name: string;
  phone: string;
  is_active?: number | boolean;
}

interface Status {
  status_id: number;
  status_name: string;
}

interface FormValues {
  customer_id: string;
  assigned_employee_id: string | null;
  priority_rank: string | null;
  description: string;
  status_id: string;
  address_id: string;
  file_name: string;
  file_type: string;
  asset_type_id: string;
  asset_id: string;
  title: string;
  urgency: string | number;
  category_id?: string;
  division_id: string;
  serial_number: string;
}

interface User {
  name: string;
}

interface RootState {
  auth: {
    user: User;
  };
}

interface UploadedFile extends File {
  preview: string;
}

interface MediaData {
  file_name: string;
  file_type: string;
  file_path: string;
}

interface TicketData {
  category_id?: string;
  description: string;
  address_id: string;
  customer_id: string;
  asset_type_id: string;
  asset_id: string;
  division_id: string | null;
  priority_rank: string | null;
  status_id: number;
  assigned_employee_id: string | null;
  title: string;
  urgency: number | null;
  assignee_history?: string;
  status_history?: any;
  status_tracker?: any;
}
interface Division {
  division_id: number;
  division_name: string;
}
const validationSchema = Yup.object({
  asset_type_id: Yup.string().required("Asset Type is required"),
  asset_id: Yup.string().required("Asset  is required"),
  customer_id: Yup.string().required("Customer is required"),
  description: Yup.string().required("Description is required"),
  address_id: Yup.string().required("Address is required"),
  title: Yup.string().required("Title is required"),
  urgency: Yup.number(),
});

const conditionValidationSchema = Yup.object({
  asset_type_id: Yup.string().required("Asset Type is required"),
  asset_id: Yup.string().required("Asset  is required"),
  customer_id: Yup.string().required("Customer is required"),
  description: Yup.string().required("Description is required"),
  address_id: Yup.string().required("Address is required"),
  priority_rank: Yup.string().required("Priority rank is required"),
});

const initialValues: FormValues = {
  customer_id: "",
  assigned_employee_id: null,
  priority_rank: null,
  description: "",
  status_id: "",
  address_id: "",
  file_name: "",
  file_type: "",
  asset_type_id: "",
  asset_id: "",
  division_id: "",
  title: "",
  urgency: "",
  serial_number: "",
};

export interface AdminAddTicketProps {
  embedded?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  /** When set (e.g. from customer Tickets overlay), customer is pre-selected; if they have one asset, asset type and asset are auto-filled */
  initialCustomerId?: number;
}

// --- Extracted: upload a single file to S3 ---
const uploadFileToS3 = async (file: UploadedFile): Promise<MediaData> => {
  const formData = new FormData();
  formData.append("file", file);

  const uploadResponse = await axios.post("/upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  const fileName =
    uploadResponse?.data?.uniqueFilename || `${Date.now()}-${file.name}`;
  const filePath = uploadResponse?.data?.location || "";
  let type = file.type.split("/")[0];
  if (type === "image") type = "Photo";
  return { file_name: fileName, file_type: type, file_path: filePath };
};

// --- Extracted: upload all files and return MediaData array ---
const uploadAllFiles = async (uploadedFiles: UploadedFile[]): Promise<MediaData[]> => {
  if (!uploadedFiles || uploadedFiles.length === 0) return [];
  return Promise.all(uploadedFiles.map(uploadFileToS3));
};

// --- Extracted: build base ticket data from form values ---
const buildTicketData = (values: FormValues): TicketData => ({
  category_id: values?.category_id,
  description: values?.description,
  address_id: values?.address_id,
  customer_id: values?.customer_id,
  asset_type_id: values?.asset_type_id,
  asset_id: values?.asset_id,
  division_id: values?.division_id || null,
  priority_rank: values?.priority_rank,
  status_id: values?.assigned_employee_id ? 2 : 1,
  assigned_employee_id: values?.assigned_employee_id,
  title: values?.title,
  urgency: values?.urgency ? Number(values?.urgency) : null,
});

// --- Extracted: attach assignee + status history to ticket data ---
const attachHistoryFields = (
  ticketData: TicketData,
  selectedEmployee: Employee | null,
  selectedStatus: Status | null,
  user: User,
  currentTime: string,
  assignedEmployeeId: string | null
): void => {
  if (selectedEmployee?.name) {
    ticketData.assignee_history = AssigneeHistory(null, selectedEmployee.name);
  }
  if (selectedStatus) {
    ticketData.status_history = StatusHistory(null, user?.name, selectedStatus.status_name);
    ticketData.status_tracker = StatusTracker({
      previousData: null,
      message: "Ticket is Created",
      statusName: selectedStatus.status_name,
      status_id: assignedEmployeeId ? 2 : 1,
      createdby: user?.name,
      employeeName: selectedEmployee?.name ?? "",
      employeePhone: selectedEmployee?.phone ?? "",
      arraivalDate: currentTime,
    });
  }
};

// --- Extracted: normalize a raw API asset into the Asset shape ---
const normalizeSerialAsset = (asset: any): Asset => ({
  asset_id: String(asset.asset_id),
  asset_name: asset.asset_name,
  address_id: asset.address_id ? String(asset.address_id) : "",
  address_type: asset.address_type ?? "",
  city_name: asset.city_name ?? "",
  region_name: asset.region_name ?? "",
  asset_type_id: asset.asset_type_id ? String(asset.asset_type_id) : undefined,
  asset_type_name: asset.asset_type_name,
  division_id: asset.division_id ?? undefined,
  division_name: asset.division_name ?? "",
  state_name: asset.state_name ?? "",
  address: asset.address ?? "",
  asset_sku: asset.asset_sku,
});

// --- Extracted: resolve asset-type option from existing list or raw asset fields ---
const resolveAssetTypeOption = (
  normalizedAsset: Asset,
  assetsData: AssetType[] | null
): AssetType | null => {
  return (
    assetsData?.find(
      (option) =>
        String(option.asset_type_id) === String(normalizedAsset.asset_type_id)
    ) ??
    (normalizedAsset.asset_type_id && normalizedAsset.asset_type_name
      ? {
          asset_type_id: normalizedAsset.asset_type_id,
          asset_type_name: normalizedAsset.asset_type_name ?? "",
        }
      : null)
  );
};

// --- Extracted: upsert an asset into a list by asset_id ---
const upsertAssetList = (list: Asset[], incoming: Asset): Asset[] => {
  const index = list.findIndex(
    (item) => String(item.asset_id) === String(incoming.asset_id)
  );
  if (index === -1) return [...list, incoming];
  const clone = [...list];
  clone[index] = { ...clone[index], ...incoming };
  return clone;
};

const AdminAddTicket: React.FC<AdminAddTicketProps> = ({
  embedded,
  onSuccess,
  onCancel,
  initialCustomerId,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    initialCustomerId != null ? String(initialCustomerId) : null
  );
  const [selectedCustomerType, setSelectedCustomerType] = useState<
    string | null
  >(null);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<Division | null>(
    null
  );
  const [selectedAssetTypeId, setSelectedAssetTypeId] = useState<string | null>(
    null
  );
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | null>(
    null
  );
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<Asset | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [serialLookupLoading, setSerialLookupLoading] = useState(false);
  const [serialLookupError, setSerialLookupError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<Status>({
    status_id: 1,
    status_name: "Open",
  });
  const { fetchError, fetchSuccess } = useSnackbar();
  const formikApiRef = React.useRef<{
    setFieldValue: (name: string, value: unknown) => void;
  } | null>(null);

  const navigate = useNavigate();
  const activeCustomers = React.useMemo(
    () =>
      (customers || []).filter(
        (entry: Customer) =>
          entry?.is_active === 1 ||
          entry?.is_active === true ||
          String(entry?.is_active) === "1"
      ),
    [customers]
  );

  const fetchActiveCustomers = useCallback(async () => {
    try {
      const customerList = await axios.get("/customer", { params: { is_active: 1 } });
      const activeList = (customerList.data.list || []).filter(
        (entry: Customer) =>
          entry?.is_active === 1 ||
          entry?.is_active === true ||
          String(entry?.is_active) === "1"
      );
      setCustomers(activeList);
    } catch (err) {
      console.error("Error fetching data", err);
    }
  }, []);

  useEffect(() => {
    fetchActiveCustomers();
  }, [fetchActiveCustomers]);

  useEffect(() => {
    if (initialCustomerId != null && customers.length > 0) {
      const customer = customers.find(
        (c) => c.customer_id === String(initialCustomerId) || Number(c.customer_id) === initialCustomerId
      );
      if (customer) {
        setSelectedCustomerId(String(customer.customer_id));
        setSelectedCustomerType(customer.type ?? "customer");
      }
    }
  }, [initialCustomerId, customers]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesResponse = await axios.get(
          `/category/asset-type/${selectedAssetTypeId}`,
          {
            params: {
              is_active: 1,
            },
          }
        );
        setCategories(categoriesResponse.data.list);
      } catch (err) {
        console.error("Error fetching data", err);
      }
    };
    fetchData();
  }, [selectedAssetTypeId]);

  useEffect(() => {
    const fetchEmployeesData = async () => {
      try {
        const employeeslist = await axios.get(`/employee/roles/4`);
        const { data } = employeeslist;
        const activeEmployees = (data?.list || []).filter(
          (entry: Employee) => Number(entry?.is_active ?? 1) === 1
        );
        setEmployees(activeEmployees);
      } catch (err) {
        console.error("Error fetching employees data:", err);
        setEmployees([]);
      }
    };

    fetchEmployeesData();
  }, []);

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const dropzone = useDropzone({
    accept: {
      "image/png": [".png", ".jpeg", ".jpg"],
      "video/mp4": [".mp4"],
    },
    onDrop: (acceptedFiles: File[]) => {
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ),
      ]);
    },
  });

  const handleDelete = (index: number) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const applyCustomerFromSerial = useCallback(
    (
      asset: any,
      customerId: string,
      customerType: string,
      setFieldValue: (field: string, value: unknown) => void
    ) => {
      setCustomers((prev) => {
        const exists = prev.some((c) => String(c.customer_id) === customerId);
        if (exists) return prev;
        return [
          ...prev,
          {
            customer_id: customerId,
            name: asset.customer_name ?? "Customer",
            type: customerType,
          },
        ];
      });
      setFieldValue("customer_id", customerId);
      setSelectedCustomerId(customerId);
      setSelectedCustomerType(customerType);
    },
    []
  );

  const applyDivisionFromSerial = useCallback(
    (asset: any, customerType: string, setFieldValue: (field: string, value: unknown) => void) => {
      if (customerType === "company" && asset.division_id) {
        const divisionOption: Division = {
          division_id: Number(asset.division_id),
          division_name: asset.division_name ?? "",
        };
        setSelectedDivisions(divisionOption);
        setFieldValue("division_id", divisionOption.division_id);
        setDivisions((prev) => {
          const exists = prev.some((div) => div.division_id === divisionOption.division_id);
          return exists ? prev : [...prev, divisionOption];
        });
      } else {
        setSelectedDivisions(null);
        setFieldValue("division_id", "");
      }
    },
    []
  );

  const applyAssetTypeFromSerial = useCallback(
    (
      normalizedAsset: Asset,
      setFieldValue: (field: string, value: unknown) => void,
      assetsDataSnapshot: AssetType[] | null
    ) => {
      const assetTypeOption = resolveAssetTypeOption(normalizedAsset, assetsDataSnapshot);
      if (assetTypeOption) {
        setSelectedAssetType(assetTypeOption);
        setSelectedAssetTypeId(assetTypeOption.asset_type_id);
        setFieldValue("asset_type_id", assetTypeOption.asset_type_id);
      } else if (normalizedAsset.asset_type_id) {
        setSelectedAssetTypeId(normalizedAsset.asset_type_id);
        setFieldValue("asset_type_id", normalizedAsset.asset_type_id);
      }
    },
    []
  );

  const [assetsData, setAssetsData] = useState<AssetType[] | null>(null);

  const handleSerialLookup = async (
    serialInput: string,
    setFieldValue: (field: string, value: unknown) => void
  ) => {
    const trimmed = serialInput?.trim();
    if (!trimmed) {
      setSerialLookupError("Enter a serial number to search");
      return;
    }
    setSerialLookupLoading(true);
    setSerialLookupError(null);

    try {
      const response = await axios.get(`/assets/serial/${encodeURIComponent(trimmed)}`);
      const asset = response?.data?.asset;
      if (!asset) throw new Error("Asset not found");

      const customerId = String(asset.customer_id ?? "");
      const customerType = asset.customer_type ?? "customer";

      applyCustomerFromSerial(asset, customerId, customerType, setFieldValue);
      applyDivisionFromSerial(asset, customerType, setFieldValue);

      const normalizedAsset = normalizeSerialAsset(asset);
      applyAssetTypeFromSerial(normalizedAsset, setFieldValue, assetsData);

      setSelectedCategory(null);
      setFieldValue("category_id", "");

      if (customerType === "company") {
        setDivisionAssets((prev) => upsertAssetList(prev, normalizedAsset));
      } else {
        setAssets((prev) => upsertAssetList(prev, normalizedAsset));
      }

      setSelectedAsset(normalizedAsset);
      setFieldValue("asset_id", normalizedAsset.asset_id);
      setSelectedAddress(normalizedAsset);
      setFieldValue("address_id", normalizedAsset.address_id || "");

      setFieldValue("serial_number", asset.asset_sku || trimmed);
      fetchSuccess("Asset details auto-filled from serial number.");
    } catch (error) {
      const message =
        error instanceof AxiosError && error.response?.data?.error
          ? error.response.data.error
          : "Asset not found for that serial number";
      setSerialLookupError(message);
      fetchError(message);
    } finally {
      setSerialLookupLoading(false);
    }
  };

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get("/asset-types", { params: { is_active: 1 } });
      const assetData =
        response.data.list && Array.isArray(response.data.list)
          ? response.data.list.filter(
              (entry: AssetType) => Number(entry?.is_active ?? 1) === 1
            )
          : [];
      setAssetsData(assetData);
    } catch (error) {
      console.error((error as any).response?.data?.error);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const [assets, setAssets] = useState<Asset[]>([]);
  const [divisionAssets, setDivisionAssets] = useState<Asset[]>([]);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(
        `/assets/${selectedAssetTypeId}/${selectedCustomerId}`
      );
      const activeAssets = (response?.data?.list || []).filter(
        (entry: Asset) => Number(entry?.is_active ?? 1) === 1
      );
      setAssets(activeAssets);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  }, [selectedAssetTypeId, selectedCustomerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const fetchAssetsByDivisionsAndAssetTypes = useCallback(async () => {
    if (
      selectedCustomerId &&
      selectedAssetType?.asset_type_id &&
      selectedDivisions?.division_id
    ) {
      try {
        const response = await axios.get(
          `/assets/${selectedDivisions?.division_id}/${selectedAssetType?.asset_type_id}/${selectedCustomerId}`
        );
        const activeAssets = (response?.data?.list || []).filter(
          (entry: Asset) => Number(entry?.is_active ?? 1) === 1
        );
        setDivisionAssets(activeAssets);
      } catch (error) {
        console.error("Error fetching assets:", error);
      }
    }
  }, [selectedCustomerId, selectedAssetType?.asset_type_id]);
  useEffect(() => {
    if (!selectedCustomerId || !selectedAssetType?.asset_type_id) return;

    if (selectedCustomerType === "customer") {
      fetchData();
    } else if (
      selectedCustomerType === "company" &&
      selectedDivisions?.division_id
    ) {
      fetchAssetsByDivisionsAndAssetTypes();
    }
  }, [
    selectedCustomerId,
    selectedAssetType?.asset_type_id,
    selectedCustomerType,
    selectedDivisions?.division_id,
  ]);
  const assetOptions =
    selectedCustomerType === "company" ? divisionAssets : assets;
  const priorityRanks: string[] = ["High", "Medium", "Low"];
  const currentTime = DateFormat();
  const fetchDivisions = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `/divisions/customer/${selectedCustomerId}`
      );
      setDivisions(response?.data?.divisions);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  }, [selectedCustomerId]);
  React.useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);

  // When customer is selected and has exactly one asset, auto-fill asset type and asset
  useEffect(() => {
    if (!selectedCustomerId) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await axios.get(`/assets/customer/${selectedCustomerId}`, {
          params: { is_active: 1 },
        });
        const list: Asset[] = (res?.data?.list ?? []).filter(
          (entry: Asset) => Number(entry?.is_active ?? 1) === 1
        );
        if (cancelled || list.length !== 1) return;
        const asset = list[0];
        const assetTypeId = asset.asset_type_id;
        const assetTypeName = asset.asset_type_name;
        const at =
          assetsData?.find(
            (a) => String(a.asset_type_id) === String(assetTypeId)
          ) ?? (assetTypeId && assetTypeName
            ? { asset_type_id: String(assetTypeId), asset_type_name: assetTypeName }
            : null);
        if (!at) return;
        const setFieldValue = formikApiRef.current?.setFieldValue;
        if (setFieldValue) {
          setFieldValue("asset_type_id", String(assetTypeId));
          setFieldValue("asset_id", asset.asset_id);
          setFieldValue("address_id", asset.address_id ?? "");
          if (selectedCustomerType === "company" && asset.division_id != null) {
            setFieldValue("division_id", String(asset.division_id));
          }
        }
        setSelectedAssetTypeId(String(assetTypeId));
        setSelectedAssetType(at);
        setSelectedAsset(asset);
        setSelectedAddress(asset);
        if (selectedCustomerType === "company" && asset.division_id != null) {
          setSelectedDivisions({
            division_id: asset.division_id,
            division_name: asset.division_name ?? "",
          });
        }
      } catch (e) {
        console.error("Error fetching customer assets for auto-fill", e);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedCustomerId, selectedCustomerType, assetsData]);

  // When asset type is selected and customer has exactly one asset for that type, auto-fill asset
  useEffect(() => {
    if (
      !selectedAssetTypeId ||
      !selectedCustomerId ||
      !assetOptions?.length ||
      assetOptions.length !== 1
    )
      return;
    const asset = assetOptions[0];
    const setFieldValue = formikApiRef.current?.setFieldValue;
    if (setFieldValue) {
      setFieldValue("asset_id", asset.asset_id);
      setFieldValue("address_id", asset.address_id ?? "");
    }
    setSelectedAsset(asset);
    setSelectedAddress(asset);
  }, [selectedAssetTypeId, selectedCustomerId, assetOptions]);

  // Fix: onSubmit cognitive complexity reduced by delegating to extracted helpers
  const handleSubmit = async (
    values: FormValues,
    { setSubmitting: formikSetSubmitting }: FormikHelpers<FormValues>
  ) => {
    setSubmitting(true);
    setLoading(true);

    const ticketData = buildTicketData(values);
    attachHistoryFields(
      ticketData,
      selectedEmployee,
      selectedStatus,
      user,
      currentTime,
      values?.assigned_employee_id
    );

    try {
      const mediaDataArray = await uploadAllFiles(uploadedFiles);
      const payload =
        mediaDataArray.length > 0
          ? { ticketData, mediaData: mediaDataArray }
          : { ticketData };

      const response = await axios.post("/tickets", payload);
      fetchSuccess(response.data.message);
      if (embedded && onSuccess) {
        onSuccess();
      } else {
        navigate("/admin/tickets-list");
      }
    } catch (error: any) {
      fetchError(
        error.response?.data?.error || error.message || "Something went wrong"
      );
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const content = (
    <Card
      style={{
        padding: embedded ? "16px" : "24px",
        width: "100%",
        borderRadius: "8px",
      }}
    >
      {!embedded && (
        <SoftTypography variant="h5" style={{ marginBottom: "18px" }}>
          Add New Ticket
        </SoftTypography>
      )}
      <Formik
        key={initialCustomerId ?? "add-ticket"}
        enableReinitialize
        initialValues={{
          ...initialValues,
          customer_id: initialCustomerId != null ? String(initialCustomerId) : "",
        }}
        validationSchema={
          selectedEmployee ? conditionValidationSchema : validationSchema
        }
        onSubmit={handleSubmit}
      >
        {({ errors, touched, setFieldValue, values }) => {
          formikApiRef.current = { setFieldValue };
          return (
            <Form
              className="new-ticket-form"
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Title
                    </SoftTypography>
                    <Field
                      name="title"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      error={!!errors.title && touched.title}
                      helperText={touched.title && errors.title}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Serial Number (optional)
                    </SoftTypography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        name="serial_number"
                        value={values.serial_number}
                        onChange={(event) => {
                          setFieldValue("serial_number", event.target.value);
                          if (serialLookupError) setSerialLookupError(null);
                        }}
                        placeholder="Enter asset serial / SKU"
                        variant="outlined"
                        fullWidth
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={serialLookupLoading}
                        onClick={() =>
                          handleSerialLookup(values.serial_number, setFieldValue)
                        }
                      >
                        {serialLookupLoading ? "Searching..." : "Fetch"}
                      </Button>
                    </Box>
                    {serialLookupError && (
                      <SoftTypography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {serialLookupError}
                      </SoftTypography>
                    )}
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Customer
                    </SoftTypography>
                    <Autocomplete
                      options={activeCustomers}
                      onOpen={fetchActiveCustomers}
                      getOptionLabel={(option) =>
                        `${option.name}${option.email ? ` - ${option.email}` : ""}`
                      }
                      value={
                        activeCustomers.find(
                          (c) =>
                            String(c.customer_id) === values.customer_id ||
                            Number(c.customer_id) === Number(values.customer_id)
                        ) ?? null
                      }
                      onChange={(event, value) => {
                        setSelectedAddress(null);
                        setFieldValue("assigned_employee_id", null);
                        setFieldValue("address_id", null);
                        setSelectedEmployee(null);
                        setFieldValue("customer_id", value?.customer_id || "");
                        setSelectedAssetType(null);
                        setSelectedAssetTypeId(null);
                        setFieldValue("asset_type_id", "");
                        setFieldValue("asset_id", "");
                        setSelectedAsset(null);
                        setSelectedCategory(null);
                        setFieldValue("category_id", "");
                        setFieldValue("division_id", "");
                        setSelectedCustomerId(value?.customer_id || null);
                        setSelectedCustomerType(value?.type || "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.customer_id && touched.customer_id}
                          helperText={touched.customer_id && errors.customer_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                {selectedCustomerType === "company" && (
                  <Grid item xs={12} sm={6}>
                    <SoftBox sx={{ display: "block" }}>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                        Division
                      </SoftTypography>
                      <Autocomplete
                        options={divisions || []}
                        getOptionLabel={(option) => option.division_name}
                        value={selectedDivisions}
                        onChange={(event, value) => {
                          setFieldValue("division_id", value?.division_id || "");
                          setSelectedDivisions(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.division_id && touched.division_id}
                            helperText={touched.division_id && errors.division_id}
                          />
                        )}
                      />
                    </SoftBox>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Asset Type
                    </SoftTypography>
                    <Autocomplete
                      options={assetsData || []}
                      getOptionLabel={(option) => `${option.asset_type_name}`}
                      value={selectedAssetType}
                      onChange={(event, value) => {
                        setFieldValue("asset_type_id", value?.asset_type_id || "");
                        setFieldValue("category_id", "");
                        setSelectedAssetType(value);
                        setSelectedAssetTypeId(value?.asset_type_id || null);
                        setSelectedCategory(null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.asset_type_id && !!touched.asset_type_id}
                          helperText={touched.asset_type_id && errors.asset_type_id}
                        />
                      )}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Assets
                    </SoftTypography>
                    <Autocomplete
                      options={assetOptions || []}
                      getOptionLabel={(option) => option.asset_name || ""}
                      value={selectedAsset}
                      onChange={(event, value) => {
                        setSelectedAddress(value);
                        setSelectedAsset(value);
                        setFieldValue("asset_id", value ? value.asset_id : "");
                        setFieldValue("address_id", value ? value.address_id : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.asset_id && !!touched.asset_id}
                          helperText={touched.asset_id && errors.asset_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Category
                    </SoftTypography>
                    <Autocomplete
                      options={categories || []}
                      getOptionLabel={(option) => option.name}
                      value={selectedCategory}
                      onChange={(event, value) => {
                        setFieldValue("category_id", value?.category_id || "");
                        setSelectedCategory(value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.category_id && touched.category_id}
                          helperText={touched.category_id && errors.category_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Assignee
                    </SoftTypography>
                    <Autocomplete
                      options={employees || []}
                      getOptionLabel={(option) => option.name}
                      value={selectedEmployee}
                      onChange={(event, value) => {
                        setFieldValue("assigned_employee_id", value?.employee_id || null);
                        setSelectedStatus(
                          value?.employee_id
                            ? { status_id: 2, status_name: "ToDo" }
                            : { status_id: 1, status_name: "Open" }
                        );
                        setSelectedEmployee(value);
                        if (!value) setSelectedEmployee(null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={
                            !!errors.assigned_employee_id &&
                            !!touched.assigned_employee_id
                          }
                          helperText={
                            touched.assigned_employee_id &&
                            errors.assigned_employee_id
                          }
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>

                {values?.assigned_employee_id ? (
                  <Grid item xs={12} sm={6}>
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Priority Rank
                      </SoftTypography>
                      <Autocomplete
                        options={priorityRanks || []}
                        getOptionLabel={(option) => option}
                        value={selectedPriority}
                        onChange={(event, value) => {
                          setFieldValue("priority_rank", value || null);
                          setSelectedPriority(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.priority_rank && !!touched.priority_rank}
                            helperText={touched.priority_rank && errors.priority_rank}
                          />
                        )}
                      />
                    </SoftBox>
                  </Grid>
                ) : null}
                {values?.priority_rank ? (
                  <Grid item xs={12} sm={6}>
                    <SoftBox>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Urgency Level (1-10)
                      </SoftTypography>
                      <Autocomplete
                        options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                        getOptionLabel={(option) => option.toString()}
                        value={values.urgency ? Number(values.urgency) : null}
                        onChange={(event, value) => {
                          setFieldValue("urgency", value || null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.urgency && touched.urgency}
                            helperText={touched.urgency && errors.urgency}
                          />
                        )}
                      />
                    </SoftBox>
                  </Grid>
                ) : null}
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Address
                    </SoftTypography>
                    <TextField
                      name="address_id"
                      fullWidth
                      value={
                        selectedAddress
                          ? `${selectedAddress?.address_type}-${selectedAddress?.city_name}-${selectedAddress?.region_name}`
                          : ""
                      }
                      variant="outlined"
                      disabled
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Description
                    </SoftTypography>
                    <Field
                      name="description"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      error={!!errors.description && touched.description}
                      helperText={touched.description && errors.description}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} md={12}>
                  {uploadedFiles.length > 0 && (
                    <div className="upload-media">
                      {uploadedFiles.map((file, index) => (
                        <div
                          key={file.name + "-" + file.lastModified}
                          className="media-container"
                          style={{ position: "relative" }}
                        >
                          {file.preview &&
                            (file.type.startsWith("image") ? (
                              <img
                                src={file.preview}
                                alt={`media-${index}`}
                                className="upload-img"
                              />
                            ) : (
                              <video
                                className="upload-video"
                                controls
                                width="100%"
                                height="140px"
                              >
                                <source src={file.preview} type="video/mp4" />
                                <track kind="captions" />
                              </video>
                            ))}
                          <IconButton onClick={() => handleDelete(index)} color="secondary">
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      ))}
                    </div>
                  )}
                  <Box
                    sx={{ cursor: "pointer", width: "fit-content" }}
                    {...dropzone.getRootProps()}
                  >
                    <Box>
                      <label htmlFor="image-upload-input">
                        <input {...dropzone.getInputProps({ name: "file_name" })} />
                        <Button
                          component="span"
                          variant="contained"
                          color="primary"
                          className="upload-btn"
                        >
                          Upload File
                        </Button>
                      </label>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Button
                    className="success-btn"
                    sx={{ position: "relative", minWidth: 100 }}
                    variant="contained"
                    type="submit"
                    disabled={submitting || loading}
                    color="success"
                  >
                    {loading ? (
                      <>
                        <CircularProgress
                          size={18}
                          color="inherit"
                          sx={{ mr: 1 }}
                        />
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                  <Button
                    className="cancel-btn"
                    sx={{ position: "relative", minWidth: 100, ml: 2.5 }}
                    variant="contained"
                    color="warning"
                    onClick={() =>
                      embedded && onCancel
                        ? onCancel()
                        : navigate("/admin/tickets-list")
                    }
                    type="button"
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Card>
  );

  if (embedded) return content;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
    </DashboardLayout>
  );
};

export default AdminAddTicket;
